// The budget panel's hook: the saved policy (or "unavailable") and one save. The write is a
// version CAS against what the person saw; the exact command (operation id included) stays with an
// unresolved attempt until the server answers it, so a retry after an unknown outcome replays the original.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import {
  BudgetOwnerOnly,
  createAppBudgetApi,
  creditsFromText,
  type AppBudgetApi,
  type BudgetCommand,
  type BudgetView,
} from '../budget-api';

export type BudgetNotice = null | 'not_authorized' | 'invalid' | 'changed' | 'unknown';

export function useAppBudget(artifactId: string, versionId: string, explicit?: AppBudgetApi) {
  const bound = useSessionBoundFetch();
  const [own] = useState(() => createAppBudgetApi(artifactId, versionId, bound));
  const api = explicit ?? own;
  const [view, setView] = useState<BudgetView | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  // The producer said, explicitly, that only the company owner sets budgets (code budget_management_required).
  // Any other refusal or failure is `unavailable`; the owner rule is never asserted from a bare 403.
  const [ownerOnly, setOwnerOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<BudgetNotice>(null);
  const pending = useRef<{ amounts: string; command: BudgetCommand } | null>(null);
  const flying = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const refresh = useCallback(async () => {
    try {
      const next = await api.read();
      if (!alive.current) return;
      setView(next);
      setUnavailable(false);
      setOwnerOnly(false);
    } catch (error) {
      if (!alive.current) return;
      const owner = error instanceof BudgetOwnerOnly;
      setOwnerOnly(owner);
      setUnavailable(!owner);
    }
  }, [api]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  /** The form's whole-credit text; false when it is not a valid pair (no call is made). */
  const save = useCallback(
    async (perRun: string, monthly: string) => {
      const perRunCredits = creditsFromText(perRun);
      const monthlyCredits = creditsFromText(monthly);
      if (flying.current || !view) return false;
      const { limits } = view;
      if (
        perRunCredits === null ||
        monthlyCredits === null ||
        perRunCredits < limits.perRun.min ||
        perRunCredits > limits.perRun.max ||
        monthlyCredits < limits.monthly.min ||
        monthlyCredits > limits.monthly.max ||
        monthlyCredits < perRunCredits
      ) {
        setNotice('invalid');
        return false;
      }
      flying.current = true;
      setBusy(true);
      setNotice(null);
      // The exact command of an UNRESOLVED attempt (same operation id, same version the person saw
      // then, same amounts) is what a repeat sends: a view refresh, even one that shows the policy moved,
      // never retargets it. If the attempt had committed the server replays its original; if another
      // write moved the policy it answers 'changed' and the next Save is a fresh command.
      const amounts = JSON.stringify([perRunCredits, monthlyCredits]);
      if (pending.current?.amounts !== amounts)
        pending.current = {
          amounts,
          command: {
            operationId: globalThis.crypto.randomUUID(),
            expectedPolicyVersion: view.policyVersion,
            perRunCredits,
            monthlyCredits,
          },
        };
      const outcome = await api.write(pending.current.command);
      flying.current = false;
      if (!alive.current) return outcome.kind === 'ok';
      setBusy(false);
      if (outcome.kind === 'ok') {
        pending.current = null;
        setView(outcome.view);
        return true;
      }
      if (outcome.kind === 'refused') pending.current = null;
      setNotice(outcome.kind === 'refused' ? outcome.reason : 'unknown');
      void refresh();
      return false;
    },
    [api, view, refresh],
  );
  return { view, unavailable, ownerOnly, busy, notice, save, refresh };
}
